/**
 * CoConnect — National Command Center (v2.7)
 * ────────────────────────────────────────────
 * "One screen, entire Cameroon, real-time" dashboard (Chapter 124 of the
 * enterprise spec). Top-level route at /command-center, accessible to
 * admin + finance roles. Single-page, scrollable sections:
 *   1. National KPIs (6 MetricTiles)
 *   2. AI Intelligence (2-column: AI Governance V2 + Cognitive Memory)
 *   3. Alerts (3-column: Disease + Weather + EUDR)
 *   4. Supply Chain (batch flow table)
 *   5. Data Source Health (table)
 *   6. Circuit Breaker Status (table)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CopilotWidget from '../components/CopilotWidget';
import LogoutButton from '../components/LogoutButton';
import ProfileButton from '../components/ProfileButton';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  BrandMark, Card, SectionHeader, MetricTile, StatusPill,
  Spinner, Alert, EmptyState, IconButton,
} from '../components/ui';
// ─── Inline icons (v2.7) ──────────────────────────────────────
const Icon = {
  Users: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 11-8 0 4 4 0 018 0zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>),
  Package: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /><path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" /></svg>),
  Truck: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>),
  Dollar: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>),
  Cpu: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /><path d="M9 2v2M15 2v2M9 20v2M15 20v2M20 9h3M20 14h3M1 9h3M1 14h3" /></svg>),
  Clock: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>),
  Refresh: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>),
  Database: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>),
  Shield: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>),
  Alert: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M10.3 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" /></svg>),
  Cloud: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" /></svg>),
  Leaf: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M11 20A7 7 0 019.8 6.1C15.5 5 17 4.48 19.5 2c-.5 4.5-.5 7.5-3 12-2 3.5-7 4-7 4zM2 21c0-3 1.85-5.36 5.08-6" /></svg>),
  Activity: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>),
  Check: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M20 6L9 17l-5-5" /></svg>),
  Zap: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>),
  ArrowLeft: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M19 12H5M12 19l-7-7 7-7" /></svg>),
};
const CommandCenter: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRole = user?.role || 'admin';
  // ─── Live clock (updates every second) ───
  const [now, setNow] = useState<Date>(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  // ─── Loading / error / refresh state ───
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  // ─── Section 1: National KPIs ───
  const [totalFarmers, setTotalFarmers] = useState<number>(0);
  const [totalBatches, setTotalBatches] = useState<number>(0);
  const [activeDispatches, setActiveDispatches] = useState<number>(0);
  const [totalPaidXaf, setTotalPaidXaf] = useState<number>(0);
  const [aiRunsCount, setAiRunsCount] = useState<number>(0);
  const [pendingApprovals, setPendingApprovals] = useState<number>(0);
  // ─── Section 2: AI Intelligence ───
  const [aiMetrics, setAiMetrics] = useState<any>(null);
  const [memoryDashboard, setMemoryDashboard] = useState<any>(null);
  // ─── Section 3: Alerts ───
  const [diseaseRuns, setDiseaseRuns] = useState<any[]>([]);
  const [weather, setWeather] = useState<any>(null);
  const [eudrReport, setEudrReport] = useState<any>(null);
  // ─── Section 4: Supply Chain ───
  const [batchFlow, setBatchFlow] = useState<any[]>([]);
  // ─── Section 5: Data Source Health ───
  const [dataSources, setDataSources] = useState<any[]>([]);
  // ─── Section 6: Circuit Breaker Status ───
  const [circuitBreakers, setCircuitBreakers] = useState<any[]>([]);
  const fetchData = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      // safeGet helper — silently falls back on 404/500 (defensive, since
      // some endpoints may not yet exist on every deployment).
      const safeGet = (url: string, fallback: any) =>
        apiClient.get(url).then((r: any) => r.data ?? fallback).catch(() => fallback);
      // Section 1: National KPIs (parallel)
      const [farmersRes, batchesRes, dispatchesRes, melRes, aiRunsRes, approvalsRes] = await Promise.all([ // v3.2.9
        safeGet('/farmers?limit=200', []), // v3.2.9: was limit=1
        safeGet('/batches?limit=200', []), // v3.2.9: was limit=1
        safeGet('/dispatches?status=DISPATCHED', []),
        safeGet('/mel/dashboard', {}), // v3.2.9: primary source for KPIs
        safeGet('/ai-agents/runs/recent?limit=100', { runs: [] }), // v3.2.9: was limit=1
        safeGet('/durability/approvals/pending', { approvals: [] }),
      ]); // v3.2.9

      // Farmers count — backend may return array OR { farmers: [], total: N } OR { count: N }
      const farmerCount = typeof melRes?.total_farmers === 'number' ? melRes.total_farmers  // v3.2.9: MEL has real count
        : Array.isArray(farmersRes) ? farmersRes.length
        : (typeof farmersRes?.total === 'number' ? farmersRes.total
        : Array.isArray(farmersRes?.farmers) ? farmersRes.farmers.length
        : Array.isArray(farmersRes?.data) ? farmersRes.data.length : 0);
      setTotalFarmers(farmerCount);
      // Batches count
      const batchCount = typeof melRes?.total_delivered === 'number' ? (melRes.total_delivered + (melRes.total_pending || 0))  // v3.2.9: MEL has real count
        : Array.isArray(batchesRes) ? batchesRes.length
        : (typeof batchesRes?.total === 'number' ? batchesRes.total
        : Array.isArray(batchesRes?.batches) ? batchesRes.batches.length
        : Array.isArray(batchesRes?.data) ? batchesRes.data.length : 0);
      setTotalBatches(batchCount);
      // Active dispatches
      const dispCount = Array.isArray(dispatchesRes) ? dispatchesRes.length
        : Array.isArray(dispatchesRes?.dispatches) ? dispatchesRes.dispatches.length
        : Array.isArray(dispatchesRes?.data) ? dispatchesRes.data.length : 0;
      setActiveDispatches(dispCount);
      // Total Paid (XAF) — MEL dashboard returns either total_payout or
      // total_paid_xaf or similar. We pick whichever exists.
      const paid = Number(melRes?.total_payout || melRes?.total_paid_xaf
        || melRes?.total_paid || melRes?.payouts_total || 0);
      setTotalPaidXaf(paid);
      // AI runs count
      const aiCount = Array.isArray(aiRunsRes?.runs) ? aiRunsRes.runs.length
        : Array.isArray(aiRunsRes) ? aiRunsRes.length
        : (typeof aiRunsRes?.total === 'number' ? aiRunsRes.total : 0);
      setAiRunsCount(aiCount);
      // Pending approvals count
      const apprCount = Array.isArray(approvalsRes?.approvals) ? approvalsRes.approvals.length
        : Array.isArray(approvalsRes) ? approvalsRes.length
        : (typeof approvalsRes?.total === 'number' ? approvalsRes.total : 0);
      setPendingApprovals(apprCount);
      // Section 2: AI Intelligence (parallel)
      const [aiMetricsRes, memoryRes] = await Promise.all([
        safeGet('/ai-governance-v2/metrics/extended', null),
        safeGet('/memory/dashboard', null),
      ]);
      setAiMetrics(aiMetricsRes);
      setMemoryDashboard(memoryRes);
      // Section 3: Alerts (parallel)
      const [diseaseRes, weatherRes, eudrRes] = await Promise.all([
        safeGet('/ai-agents/DISEASE/runs?limit=5', { runs: [] }),
        safeGet('/weather/current?lat=3.87&lng=11.52', null), // v3.2.9: was /weather (404)
        safeGet('/eudr/compliance-report', null),
      ]);
      setDiseaseRuns(Array.isArray(diseaseRes?.runs) ? diseaseRes.runs
        : Array.isArray(diseaseRes) ? diseaseRes : []);
      setWeather(weatherRes);
      setEudrReport(eudrRes);
      // Section 4: Supply Chain (batch flow)
      const batchesFlowRes = await safeGet('/batches?limit=20', []);
      const flowList = Array.isArray(batchesFlowRes) ? batchesFlowRes
        : Array.isArray(batchesFlowRes?.batches) ? batchesFlowRes.batches
        : Array.isArray(batchesFlowRes?.data) ? batchesFlowRes.data : [];
      setBatchFlow(flowList);
      // Section 5: Data Source Health
      const sourcesRes = await safeGet('/data-sources', []);
      const sourcesList = Array.isArray(sourcesRes) ? sourcesRes
        : Array.isArray(sourcesRes?.sources) ? sourcesRes.sources
        : Array.isArray(sourcesRes?.data) ? sourcesRes.data : [];
      setDataSources(sourcesList);
      // Section 6: Circuit Breaker Status
      const breakersRes = await safeGet('/durability/circuit-breakers', []);
      const breakersList = Array.isArray(breakersRes) ? breakersRes
        : Array.isArray(breakersRes?.breakers) ? breakersRes.breakers
        : Array.isArray(breakersRes?.data) ? breakersRes.data : [];
      setCircuitBreakers(breakersList);
      setLastUpdated(new Date());
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load Command Center data');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);
  // Initial load + auto-refresh every 60s
  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ─── Helpers ───
  const fmtNum = (n: number) => Number(n || 0).toLocaleString();
  const fmtXaf = (n: number) => `${Number(n || 0).toLocaleString()} XAF`;
  const fmtTime = (d: string | number | Date) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleString(); } catch { return '—'; }
  };
  const pct = (v: any, digits = 1) => {
    const n = Number(v || 0);
    return isNaN(n) ? '—' : `${n.toFixed(digits)}%`;
  };
  // ─── Loading screen (first paint) ───
  if (loading) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <BrandMark size="lg" accent="analytics" />
          <div className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
            <Spinner size="sm" /> Initializing National Command Center…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 pb-24 md:pb-0">
      <a href="#command-main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-ink-900 focus:text-white focus:rounded-lg">Skip to Command Center content</a>
      {/* ─── Header ─── */}
      <header className="topbar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <BrandMark size="md" accent="analytics" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate flex items-center gap-2">
                CoConnect National Command Center
                <span className="hidden md:inline-flex pill-success">
                  <span className="live-dot" /> Live
                </span>
              </p>
              <p className="text-xs text-ink-500 dark:text-ink-400 truncate nums">
                Updated {lastUpdated.toLocaleTimeString()} · {user?.name || userRole}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 surface-card rounded-lg text-sm font-mono nums text-ink-700 dark:text-ink-200">
              <Icon.Clock className="w-3.5 h-3.5 text-ink-400" />
              <span>{now.toLocaleTimeString()}</span>
            </div>
            <button
              onClick={fetchData}
              disabled={refreshing}
              className="btn-secondary text-xs hide-mobile"
            >
              <Icon.Refresh className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Syncing…' : 'Refresh'}</span>
            </button>
            <IconButton label="Refresh" onClick={fetchData} disabled={refreshing} className="md:hidden">
              <Icon.Refresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </IconButton>
            {userRole === 'admin' && (
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className="btn-secondary text-xs portal-back-admin"
                aria-label="Back to Admin"
              >
                <Icon.ArrowLeft className="w-4 h-4" /> <span>Back to Admin</span>
              </button>
            )}
            <ProfileButton accent="analytics" />
            <LogoutButton />
          </div>
        </div>
      </header>

      <main id="command-main-content" className="max-w-7xl mx-auto px-4 md:px-6 pt-6 pb-6 space-y-6">
        {error && (
          <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>
        )}

        <nav className="command-section-nav" aria-label="Command Center sections">
          <a href="#command-kpis" className="command-section-link"><Icon.Activity className="w-4 h-4" /><span>National KPIs</span></a>
          <a href="#command-ai" className="command-section-link"><Icon.Cpu className="w-4 h-4" /><span>AI Intelligence</span></a>
          <a href="#command-alerts" className="command-section-link"><Icon.Alert className="w-4 h-4" /><span>Alerts</span></a>
          <a href="#command-supply-chain" className="command-section-link"><Icon.Truck className="w-4 h-4" /><span>Supply Chain</span></a>
          <a href="#command-data-health" className="command-section-link"><Icon.Database className="w-4 h-4" /><span>Data Health</span></a>
          <a href="#command-circuit-breakers" className="command-section-link"><Icon.Zap className="w-4 h-4" /><span>Circuit Breakers</span></a>
        </nav>
        {/* ═══ Section 1: National KPIs (6 MetricTiles) ═══ */}
        <section id="command-kpis" className="command-section-anchor">
          <SectionHeader
            eyebrow="Section 1"
            title="National KPIs"
            subtitle="Real-time counters across the entire CoConnect network"
            icon={<Icon.Activity className="w-4 h-4" />}
            className="mb-3"
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <MetricTile
              label="Total Farmers"
              value={fmtNum(totalFarmers)}
              icon={<Icon.Users className="w-4 h-4" />}
              accent="agrarian"
            />
            <MetricTile
              label="Total Batches"
              value={fmtNum(totalBatches)}
              icon={<Icon.Package className="w-4 h-4" />}
              accent="cocoa"
            />
            <MetricTile
              label="Active Dispatches"
              value={fmtNum(activeDispatches)}
              icon={<Icon.Truck className="w-4 h-4" />}
              accent="cargo"
              hint="In transit"
            />
            <MetricTile
              label="Total Paid (XAF)"
              value={fmtNum(totalPaidXaf)}
              icon={<Icon.Dollar className="w-4 h-4" />}
              accent="analytics"
              hint="Cumulative"
            />
            <MetricTile
              label="AI Recommendations"
              value={fmtNum(aiRunsCount)}
              icon={<Icon.Cpu className="w-4 h-4" />}
              accent="admin"
              hint="Recent runs"
            />
            <MetricTile
              label="Pending Approvals"
              value={fmtNum(pendingApprovals)}
              icon={<Icon.Shield className="w-4 h-4" />}
              accent="terrain"
              hint="Awaiting review"
            />
          </div>
        </section>

        {/* ═══ Section 2: AI Intelligence (2-column) ═══ */}
        <section id="command-ai" className="command-section-anchor">
          <SectionHeader
            eyebrow="Section 2"
            title="AI Intelligence"
            subtitle="AI Governance V2 metrics + Cognitive Memory dashboard"
            icon={<Icon.Cpu className="w-4 h-4" />}
            className="mb-3"
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: AI Performance Summary */}
            <Card padding="md">
              <SectionHeader
                eyebrow="AI Governance V2"
                title="AI Performance Summary"
                icon={<Icon.Cpu className="w-4 h-4" />}
                className="mb-3"
              />
              {!aiMetrics ? (
                <EmptyState icon={<Icon.Cpu className="w-5 h-5" />} title="No AI metrics available" />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 surface-inset rounded-lg">
                    <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Citation Coverage</p>
                    <p className="mt-1 text-xl font-semibold nums text-ink-900 dark:text-ink-50">
                      {pct(aiMetrics?.citation_coverage ?? aiMetrics?.citations?.coverage, 1)}
                    </p>
                  </div>
                  <div className="p-3 surface-inset rounded-lg">
                    <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Hallucination Rate</p>
                    <p className="mt-1 text-xl font-semibold nums text-danger-600 dark:text-danger-500">
                      {pct(aiMetrics?.hallucination_rate ?? aiMetrics?.hallucinations?.rate, 2)}
                    </p>
                  </div>
                  <div className="p-3 surface-inset rounded-lg">
                    <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Avg Confidence</p>
                    <p className="mt-1 text-xl font-semibold nums text-success-600 dark:text-success-500">
                      {pct(aiMetrics?.avg_confidence ?? aiMetrics?.confidence?.avg, 1)}
                    </p>
                  </div>
                  <div className="p-3 surface-inset rounded-lg">
                    <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Total Runs</p>
                    <p className="mt-1 text-xl font-semibold nums text-ink-900 dark:text-ink-50">
                      {fmtNum(aiMetrics?.total_runs ?? aiMetrics?.runs?.total ?? 0)}
                    </p>
                  </div>
                </div>
              )}
            </Card>
            {/* Right: Memory System Summary */}
            <Card padding="md">
              <SectionHeader
                eyebrow="Cognitive Memory"
                title="Memory System Summary"
                icon={<Icon.Database className="w-4 h-4" />}
                className="mb-3"
              />
              {!memoryDashboard ? (
                <EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No memory data available" />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 surface-inset rounded-lg">
                    <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Total Events</p>
                    <p className="mt-1 text-xl font-semibold nums text-ink-900 dark:text-ink-50">
                      {fmtNum(memoryDashboard?.total_events ?? memoryDashboard?.events?.total ?? 0)}
                    </p>
                  </div>
                  <div className="p-3 surface-inset rounded-lg">
                    <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Farms w/ Memory</p>
                    <p className="mt-1 text-xl font-semibold nums text-ink-900 dark:text-ink-50">
                      {fmtNum(memoryDashboard?.farms_with_memory ?? memoryDashboard?.farms ?? 0)}
                    </p>
                  </div>
                  <div className="p-3 surface-inset rounded-lg">
                    <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Ecosystem Patterns</p>
                    <p className="mt-1 text-xl font-semibold nums text-ink-900 dark:text-ink-50">
                      {fmtNum(memoryDashboard?.ecosystem_patterns ?? memoryDashboard?.patterns ?? 0)}
                    </p>
                  </div>
                  <div className="p-3 surface-inset rounded-lg">
                    <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Avg AI Accuracy</p>
                    <p className="mt-1 text-xl font-semibold nums text-success-600 dark:text-success-500">
                      {pct(memoryDashboard?.avg_ai_accuracy ?? memoryDashboard?.accuracy?.avg, 1)}
                    </p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </section>

        {/* ═══ Section 3: Alerts (3-column) ═══ */}
        <section id="command-alerts" className="command-section-anchor">
          <SectionHeader
            eyebrow="Section 3"
            title="Alerts"
            subtitle="Disease agent runs · current weather · EUDR compliance"
            icon={<Icon.Alert className="w-4 h-4" />}
            className="mb-3"
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Disease Alerts */}
            <Card padding="md">
              <SectionHeader
                eyebrow="AI Agent"
                title="Disease Alerts"
                icon={<Icon.Leaf className="w-4 h-4" />}
                className="mb-3"
              />
              {diseaseRuns.length === 0 ? (
                <EmptyState icon={<Icon.Leaf className="w-5 h-5" />} title="No recent disease agent runs" />
              ) : (
                <div className="space-y-2">
                  {diseaseRuns.map((r: any, i: number) => (
                    <div key={r.id || i} className="p-2.5 surface-inset rounded-lg">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-medium text-ink-900 dark:text-ink-50 truncate">
                          {r.output?.diagnosis || r.output?.disease || `Run ${String(r.id || i).slice(0, 8)}`}
                        </p>
                        {typeof r.confidence === 'number' && (
                          <StatusPill
                            tone={r.confidence >= 75 ? 'success' : r.confidence >= 50 ? 'warning' : 'danger'}
                            dot
                          >
                            {r.confidence.toFixed(0)}%
                          </StatusPill>
                        )}
                      </div>
                      <p className="text-2xs text-ink-500 dark:text-ink-400 nums mt-1">
                        {fmtTime(r.created_at || r.started_at || r.timestamp)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            {/* Weather Alerts */}
            <Card padding="md">
              <SectionHeader
                eyebrow="Live"
                title="Weather Alerts"
                icon={<Icon.Cloud className="w-4 h-4" />}
                className="mb-3"
              />
              {!weather ? (
                <EmptyState icon={<Icon.Cloud className="w-5 h-5" />} title="No weather data available" />
              ) : (
                <div className="space-y-2">
                  <div className="p-3 surface-inset rounded-lg">
                    <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Conditions</p>
                    <p className="mt-1 text-sm font-semibold text-ink-900 dark:text-ink-50">
                      {weather.condition || weather.description || weather.summary || '—'}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2.5 surface-inset rounded-lg">
                      <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Temp</p>
                      <p className="mt-1 text-sm font-semibold nums text-ink-900 dark:text-ink-50">
                        {weather.temperature ?? weather.temp ?? '—'}°C
                      </p>
                    </div>
                    <div className="p-2.5 surface-inset rounded-lg">
                      <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Humidity</p>
                      <p className="mt-1 text-sm font-semibold nums text-ink-900 dark:text-ink-50">
                        {weather.humidity != null ? `${weather.humidity}%` : '—'}
                      </p>
                    </div>
                  </div>
                  {weather.advisory && (
                    <Alert tone="info" className="mt-2">{weather.advisory}</Alert>
                  )}
                  {weather.alerts && Array.isArray(weather.alerts) && weather.alerts.length > 0 && (
                    <div className="space-y-1.5 mt-2">
                      {weather.alerts.map((a: any, i: number) => (
                        <Alert key={i} tone="warning" title={a.title || 'Weather Alert'}>{a.message || a.description || ''}</Alert>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
            {/* EUDR Alerts */}
            <Card padding="md">
              <SectionHeader
                eyebrow="Compliance"
                title="EUDR Alerts"
                icon={<Icon.Shield className="w-4 h-4" />}
                className="mb-3"
              />
              {!eudrReport ? (
                <EmptyState icon={<Icon.Shield className="w-5 h-5" />} title="No EUDR compliance data" />
              ) : (
                <div className="space-y-2">
                  <div className="p-3 surface-inset rounded-lg">
                    <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Compliance Status</p>
                    <div className="mt-1">
                      <StatusPill
                        tone={
                          (eudrReport.status || eudrReport.compliance_status || '').toUpperCase() === 'COMPLIANT' ? 'success'
                          : (eudrReport.status || eudrReport.compliance_status || '').toUpperCase() === 'NON_COMPLIANT' ? 'danger'
                          : 'warning'
                        }
                        dot
                      >
                        {eudrReport.status || eudrReport.compliance_status || 'Under Review'}
                      </StatusPill>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2.5 surface-inset rounded-lg">
                      <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Compliant</p>
                      <p className="mt-1 text-sm font-semibold nums text-success-600 dark:text-success-500">
                        {fmtNum(eudrReport.compliant ?? eudrReport.compliant_count ?? 0)}
                      </p>
                    </div>
                    <div className="p-2.5 surface-inset rounded-lg">
                      <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Non-Compliant</p>
                      <p className="mt-1 text-sm font-semibold nums text-danger-600 dark:text-danger-500">
                        {fmtNum(eudrReport.non_compliant ?? eudrReport.non_compliant_count ?? 0)}
                      </p>
                    </div>
                  </div>
                  {eudrReport.compliance_rate != null && (
                    <div className="p-2.5 surface-inset rounded-lg">
                      <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Compliance Rate</p>
                      <p className="mt-1 text-sm font-semibold nums text-ink-900 dark:text-ink-50">
                        {pct(eudrReport.compliance_rate, 1)}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        </section>

        {/* ═══ Section 4: Supply Chain (table) ═══ */}
        <section id="command-supply-chain" className="command-section-anchor">
          <SectionHeader
            eyebrow="Section 4"
            title="Supply Chain"
            subtitle="Live batch flow — last 20 batches across the network"
            icon={<Icon.Package className="w-4 h-4" />}
            className="mb-3"
          />
          <Card padding="none" className="overflow-hidden">
            {batchFlow.length === 0 ? (
              <div className="p-8">
                <EmptyState icon={<Icon.Package className="w-5 h-5" />} title="No batches in flow" description="Recent batches will appear here." />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Batch #</th>
                      <th>Farmer</th>
                      <th>Weight</th>
                      <th>Grade</th>
                      <th>Status</th>
                      <th>Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchFlow.map((b: any, i: number) => (
                      <tr key={b.id || i}>
                        <td className="text-xs font-mono">{b.batch_number || b.id?.slice(0, 8) || '—'}</td>
                        <td className="text-sm">{b.farmer_name || b.farmer || '—'}</td>
                        <td className="nums">{fmtNum(b.actual_weight || b.est_weight || b.weight || 0)} kg</td>
                        <td>
                          {b.quality_grade ? (
                            <StatusPill
                              tone={b.quality_grade === 'A' ? 'success' : b.quality_grade === 'B' ? 'warning' : 'danger'}
                            >
                              {b.quality_grade}
                            </StatusPill>
                          ) : '—'}
                        </td>
                        <td><StatusPill tone="info" dot>{b.status || '—'}</StatusPill></td>
                        <td>
                          {b.payment_status ? (
                            <StatusPill
                              tone={
                                (b.payment_status || '').toUpperCase() === 'PAID' ? 'success'
                                : (b.payment_status || '').toUpperCase() === 'PENDING' ? 'warning'
                                : 'neutral'
                              }
                              dot
                            >
                              {b.payment_status}
                            </StatusPill>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </section>

        {/* ═══ Section 5: Data Source Health (table) ═══ */}
        <section id="command-data-health" className="command-section-anchor">
          <SectionHeader
            eyebrow="Section 5"
            title="Data Source Health"
            subtitle="Sync status + record counts for all 43 catalogued data sources"
            icon={<Icon.Database className="w-4 h-4" />}
            className="mb-3"
          />
          <Card padding="none" className="overflow-hidden">
            {dataSources.length === 0 ? (
              <div className="p-8">
                <EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No data sources registered" description="Registered data sources will appear here." />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Last Sync</th>
                      <th>Status</th>
                      <th>Records</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataSources.map((s: any, i: number) => {
                      const status = (s.status || s.sync_status || s.health || 'UNKNOWN').toUpperCase();
                      const tone = status === 'HEALTHY' || status === 'ACTIVE' || status === 'OK' ? 'success'
                        : status === 'DEGRADED' || status === 'STALE' ? 'warning'
                        : status === 'UNHEALTHY' || status === 'ERROR' || status === 'FAILED' ? 'danger' : 'neutral';
                      return (
                        <tr key={s.id || s.code || i}>
                          <td className="text-sm font-medium">{s.name || s.source_name || s.code || '—'}</td>
                          <td className="text-2xs nums">{fmtTime(s.last_sync || s.last_synced_at || s.updated_at)}</td>
                          <td><StatusPill tone={tone as any} dot>{status}</StatusPill></td>
                          <td className="nums">{fmtNum(s.records || s.record_count || s.total_records || 0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </section>

        {/* ═══ Section 6: Circuit Breaker Status ═══ */}
        <section id="command-circuit-breakers" className="command-section-anchor">
          <SectionHeader
            eyebrow="Section 6"
            title="Circuit Breaker Status"
            subtitle="Open / half-open circuits across all AI services + ERP integrations"
            icon={<Icon.Zap className="w-4 h-4" />}
            className="mb-3"
          />
          <Card padding="md">
            {circuitBreakers.length === 0 ? (
              <EmptyState
                icon={<Icon.Check className="w-5 h-5" />}
                title="All circuits healthy"
                description="No open or half-open circuit breakers detected."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>State</th>
                      <th>Failures</th>
                      <th>Successes</th>
                      <th>Avg Latency</th>
                      <th>Last Failure</th>
                    </tr>
                  </thead>
                  <tbody>
                    {circuitBreakers.map((b: any, i: number) => {
                      const state = (b.state || b.status || 'CLOSED').toUpperCase();
                      const tone = state === 'OPEN' ? 'danger'
                        : state === 'HALF_OPEN' ? 'warning' : 'success';
                      return (
                        <tr key={b.service_code || b.id || i}>
                          <td className="text-sm font-medium">{b.service_code || b.service || b.name || '—'}</td>
                          <td><StatusPill tone={tone as any} dot>{state}</StatusPill></td>
                          <td className="nums">{fmtNum(b.failure_count ?? b.failures ?? 0)}</td>
                          <td className="nums">{fmtNum(b.success_count ?? b.successes ?? 0)}</td>
                          <td className="nums">{fmtNum(b.avg_latency_ms ?? b.avg_latency ?? 0)} ms</td>
                          <td className="text-2xs nums">{fmtTime(b.last_failure_at || b.last_failure)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </section>
      </main>

      <CopilotWidget userRole={userRole} />
    </div>
  );
};

export default CommandCenter;